import {
  ehrLaunchRegionValueAtom,
  getEhrLaunchRegion,
} from '@/atoms/ehrLaunchRegionAtom';
import { getWidgetRegion, widgetRegionAtom } from '@/atoms/integrationAuthAtoms';
import { type QueryUserKindeProperties } from '@/context/kinde-properties-context';
import { toValidRegion } from '@/helpers/toValidRegion';
import {
  GET_KINDE_PROPERTIES,
  useQueryKindeProperties,
} from '@/hooks/query/useQueryKindeProperties';
import { getRegionFromHost, isRegionalRoutingEnabled } from '@/lib/region-routing';
import { store, type RootState } from '@/store';
import { getInvitationDataFromURL } from '@/utils/getInvitationDataFromURL/getInvitationDataFromURL';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { extractRegionFromAccessToken } from '@heidi/auth/tokens';
import { Region } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

export const defaultRegion = Region.AU;

const getRegionFromKinde = () => {
  const kindeProperties = reactQueryClient.getQueryData<QueryUserKindeProperties>([
    GET_KINDE_PROPERTIES,
  ]);

  return toValidRegion(kindeProperties?.region);
};

const getSearchParam = (key: string) => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
};

/**
 * Get the region from the hostname if on a specific subdomain.
 * For global/base domain, returns undefined.
 */
const getHostnameRegion = (): Region | undefined => {
  if (typeof window === 'undefined' || !isRegionalRoutingEnabled()) return undefined;
  return toValidRegion(getRegionFromHost(window.location.hostname));
};

/**
 * Returns true when the resolved user region is US.
 * Used to gate features that are only available in the US market
 * (e.g., AI-generated clinical suggestion chips, full shortcut library).
 */
export const isUSMarket = (): boolean => getRegion() === Region.US;

/**
 * ⚠️ COMPLIANCE WARNING (APP-6111 C10): `getRegion()` / `useRegion()` **fail
 * open to AU** and honour the `?region=` query param, so they can be spoofed
 * and never resolve to "unknown". They MUST NOT gate any
 * compliance-sensitive / medical-device decision — most importantly the
 * region-aware desktop updater (APP-6113), which must fail **closed**.
 *
 * For those gates use `kindeProperties.region` (via `useQueryKindeProperties`)
 * directly, treating `undefined` as "no update / no action".
 *
 * These helpers remain valid for non-compliance UX (feature flags, market
 * gating, etc.).
 *
 * EVERY slot below goes through `toValidRegion`, so the value returned is
 * always a canonical `Region` or the AU default — never a string that merely
 * has the type. That matters because the return value indexes
 * `scribeDomains[region]` to pick the API host, and a miss there fail-opens to
 * AU a second time: a slot carrying `'uk'` resolves "successfully" and still
 * addresses a UK clinician's data to the AU backend, with nothing logged.
 *
 * Several slots are populated by a CAST rather than a check and so cannot be
 * trusted for being typed `Region` — `widgetRegionAtom` is the widest, since
 * the widget-auth launch reads `?region=` off the URL, casts it, and persists
 * it. Normalising at the reader is what makes the chain's output canonical
 * regardless of which writer filled the slot.
 */
export const getRegion = (fallbackRegion?: Region): Region => {
  const storeState = store.getState();
  const widgetRegion = toValidRegion(getWidgetRegion());
  // Sits immediately above `userRegion` in BOTH chains. An EHR / SMART-on-FHIR
  // launch is the one caller whose region survives nothing below this slot:
  // its token carries no Kinde region claim, and its `?region=` param and
  // five-minute cookie are gone by the time the clinician reloads.
  const ehrLaunchRegion = toValidRegion(getEhrLaunchRegion());
  const userRegion = toValidRegion(storeState.auth.userRegion);
  const { region: invitationRegionString } = getInvitationDataFromURL();
  const paramsRegion = toValidRegion(getSearchParam('region'));
  const invitationRegion = toValidRegion(invitationRegionString);
  const kindeRegion = getRegionFromKinde();

  // Extract region from the access token as a fallback. Covers the window
  // between sign-in (token available in Redux) and the first useEffect that
  // persists userRegion, preventing early calls from defaulting to AU.
  const token = storeState.auth.token;
  const tokenRegion = token
    ? toValidRegion(extractRegionFromAccessToken(token))
    : undefined;

  return (
    paramsRegion ??
    widgetRegion ??
    ehrLaunchRegion ??
    userRegion ??
    kindeRegion ??
    invitationRegion ??
    tokenRegion ??
    getHostnameRegion() ??
    fallbackRegion ??
    defaultRegion
  );
};

/**
 * The region the auth slice carries, else the claim mirrored onto the access
 * token — the slot the launch, onboarding and country writers fill, and the
 * only one that resolves on desktop, where the session cache reports no region
 * (the Kinde and Auth0 desktop readers return a bare token) and nothing writes
 * the slice from the claim.
 *
 * Both halves are raw and normalised here with `toValidRegion`, the slice one
 * because the launch and query-param writers dispatch casts. An accessor
 * rather than a read at the consumer because the Rematch ratchet freezes new
 * `@/store` and `react-redux` importers, and this file is grandfathered for
 * both.
 */
export const useAuthSliceOrTokenRegion = (): Region | undefined => {
  const userRegion = useSelector((state: RootState) => state.auth.userRegion);
  const token = useSelector((state: RootState) => state.auth.token);

  return (
    toValidRegion(userRegion) ??
    toValidRegion(token ? extractRegionFromAccessToken(token) : undefined)
  );
};

export const useRegion = (): Region => {
  const widgetRegion = toValidRegion(useAtomValue(widgetRegionAtom));
  // Same slot as in `getRegion` above, and deliberately the same atom that
  // `getEhrLaunchRegion()` reads: these are two implementations of one
  // precedence, so the slot's rules live in one place rather than in both.
  const ehrLaunchRegion = toValidRegion(useAtomValue(ehrLaunchRegionValueAtom));
  const userRegion = toValidRegion(
    useSelector((state: RootState) => state.auth.userRegion)
  );
  const token = useSelector((state: RootState) => state.auth.token);
  const { region: invitationRegionString } = getInvitationDataFromURL();
  const paramsRegion = toValidRegion(getSearchParam('region'));
  const invitationRegion = toValidRegion(invitationRegionString);
  const { data: kindeProperties } = useQueryKindeProperties();
  const kindeRegion = toValidRegion(kindeProperties?.region);
  const tokenRegion = token
    ? toValidRegion(extractRegionFromAccessToken(token))
    : undefined;

  return (
    paramsRegion ??
    widgetRegion ??
    ehrLaunchRegion ??
    userRegion ??
    kindeRegion ??
    invitationRegion ??
    tokenRegion ??
    getHostnameRegion() ??
    defaultRegion
  );
};
